import type {
	PersonalLoanLeadCalcRequest,
	PersonalLoanLeadCalcResponse,
	PersonalLoanLeadFormFields,
	PersonalLoanLeadFormState,
} from "@component/PersonalLoan/LeadForm";
import { LeadForm } from "@component/PersonalLoan/LeadForm";
import type { LeadRequest } from "@component/assets/AbstractLeadForm/types";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import dayjs from "dayjs";
import type { ReactNode } from "react";
import React from "react";
import { calculate, responseToResult } from "../Calculator/domain";
import type { PersonalLoanRequest, PersonalLoanResponseItem } from "../Calculator/types";
import * as Step from "./Steps";
import "./style.scss";

export interface PersonalLoanSmallCalculatorFormFields extends PersonalLoanLeadFormFields {
	maturity: number;
}

export interface PersonalLoanSmallCalculatorState
	extends PersonalLoanLeadFormState<PersonalLoanSmallCalculatorFormFields> {
	result: PersonalLoanResponseItem;
}

/**
 * @Shortcode
 *
 * @title "Személyi hitel kalkulátor"
 * @shortcode "personal_loans_small"
 * @slug "szemelyi-hitel"
 * @type "calculator"
 */
export class PersonalLoanSmallCalculator extends LeadForm<
	PersonalLoanSmallCalculatorFormFields,
	PersonalLoanSmallCalculatorState
> {
	// EventParams
	protected readonly productTypes: string = "PERSONAL_LOAN";
	protected readonly componentType: string = "small-calculator";

	public static defaultProps = {
		outsideTitle: "Személyi hitel kalkulátor",
		outsideTitleLink: "/szemelyi-kolcson/",
		title: "Kérd szakértőnk segítségét!",
		bottomLogo: true,
	};

	protected getInitialState(): PersonalLoanSmallCalculatorState {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			result: undefined,
		} as unknown as PersonalLoanSmallCalculatorState;
	}

	protected renderSteps(): ReactNode {
		const { variant } = this.props;
		const { result, submitState, values, leadId } = this.state;
		const response = this.getCalculatorResponse();

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" />
				<Step.Khr stepName="khr" />
				<Step.Step2 stepName="step-2" defaultTarget="result" canStepBack submitState={submitState} />
				<Step.Error stepName="error" defaultTarget="step-2" canStepBack />
				<Step.Result
					stepName="result"
					defaultTarget="lead-form"
					result={result}
					allValues={values}
					className="personal-loan-result"
					canStepBack
				/>
				<Step.LeadForm
					stepName="lead-form"
					submitState={submitState}
					defaultTarget="success-lead"
					button="Tájékoztatást kérek"
					title="Kérd részletes eredményeidet email-ben és várd szakértőnk hívását, hogy segítsen a felmerülő kérdések tisztázásában!"
					loanAmount={values.loanAmount}
					maturityYears={undefined}
					installment={undefined}
					apr={undefined}
					variant={variant}
					crossSellOption
				/>
				<Step.SuccessPage
					stepName="success-lead"
					leadId={leadId}
					userEmail={values.email}
					ga4SendEventParameters={{
						providerName: response?.bankName,
						productName: response?.productName,
						productId: response?.id,
					}}
				/>
			</>
		);
	}

	private getSearchParams(): PersonalLoanRequest {
		const {
			values: { loanAmount, maturity, incomeList },
		} = this.state;

		const employmentMonths = incomeList.reduce((acc, incomeItem) => {
			const durationMonths = dayjs().diff(incomeItem.incomeStart, "month");

			return durationMonths > acc ? durationMonths : acc;
		}, 0);

		return {
			loanAmount,
			maturityYears: maturity,
			incomeList: [
				...incomeList.map((incomeItem) => ({
					...incomeItem,
					durationMonths: dayjs().diff(incomeItem.incomeStart, "month"),
					isCash: !incomeItem.receiptAmount,
				})),
			],
			employmentMonths,
			loanGoals: ["SZABAD_FELHASZNALAS"],
			fasterAdministration: undefined,
			activeBankContact: true,
			age: 30,
			comparator: "APR",
			hasPermanentHungarianAddress: true,
			roleCode: "BM",
		};
	}

	protected onStep(target: string, withEvent: boolean): void {
		const {
			values: { inKHR },
		} = this.state;
		switch (target) {
			case "step-2":
				if (inKHR) {
					super.onStep("khr", withEvent);
				} else {
					super.onStep(target, withEvent);
				}
				break;
			case "result":
				this.onResult(target, withEvent);
				break;

			default:
				super.onStep(target, withEvent);
				break;
		}
	}

	@autobind
	private onResult(target: string, withEvent: boolean): void {
		this.setState({
			submitState: "loading",
		});

		const request = this.getSearchParams();
		this.promisePool
			.add(calculate(request))
			.then((response) => {
				const results = responseToResult(request, response, true, "without-ct");

				if (!results?.normalList.length) {
					super.onStep("error", withEvent);

					this.sendCalculationEvent(["Sajnos nem találtunk a megadott feltételeknek megfelelő hitelt."]);
				} else {
					this.setState(
						{
							result: results.normalList[0],
							submitState: "done",
						},
						() => {
							super.onStep(target, withEvent);
						},
					);

					this.sendCalculationEvent();
				}
			})
			.catch((error: Error) => {
				this.setState({
					submitState: "error",
				});

				logError(error);
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({
						submitState: undefined,
					});
				});
			});
	}

	@autobind
	protected getLeadData(): LeadRequest<PersonalLoanLeadCalcRequest, PersonalLoanLeadCalcResponse> {
		const { result, values } = this.state;
		const data = super.getLeadData();
		data.response = { ...data.response, ...result };
		data.request = { ...data.request, maturityYears: values.maturity };

		return data;
	}

	protected getClassName(): string {
		return `${super.getClassName()} personal-loan-small-calculator`;
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.PersonalLoanSmallCalculator`;
	}
}

export default PersonalLoanSmallCalculator;
